import { createSelector } from '@ngrx/store';

import { AppState } from 'store/root-reducer';

import { LatestVerificationConclusion, VerificationType } from 'modules/shared/models/record.models';
import {
  VerificationConclusionsResponse, RecordOcrsResponse, RecordFaceComparesResponse
} from 'store/models/record.models';
import {
  RiskAssessmentActions,
  RiskAssessmentActionTypes
} from 'store/actions/risk-assessment.actions';
import { RiskAssessmentError, RiskReport } from 'modules/shared/models/risk-assessment.models';
import { RiskReportResponse, ApprovalStatusResponse } from 'store/models/risk-assessment.models';

export interface State {
  approvalStatuses: ApprovalStatusResponse | null;
  riskReports: RiskReportResponse | null;
  ocrResults: RecordOcrsResponse | null;
  faceResults: RecordFaceComparesResponse | null;
  ocrConclusions: VerificationConclusionsResponse | null;
  faceConclusions: VerificationConclusionsResponse | null;
  latestOcrConclusions: LatestVerificationConclusion | null;
  latestFaceConclusions: LatestVerificationConclusion | null;
  loading: Array<RiskAssessmentActionTypes>;
  err: Array<RiskAssessmentError>;
}

const initialState: State = {
  approvalStatuses: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  riskReports: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  ocrResults: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  faceResults: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  ocrConclusions: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  faceConclusions: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  latestOcrConclusions: null,
  latestFaceConclusions: null,
  loading: [],
  err: []
};

export function reducer(
  state: State = initialState,
  action: RiskAssessmentActions
): State {
  switch (action.type) {
    case RiskAssessmentActionTypes.GET_APPROVAL_STATUS_SUCCESS: {
      return {
        ...state,
        approvalStatuses: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_APPROVAL_STATUS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_APPROVAL_STATUS)
      };
    }
    case RiskAssessmentActionTypes.GET_APPROVAL_STATUS_FAILURE: {
      return {
        ...state,
        approvalStatuses: null,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_APPROVAL_STATUS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_APPROVAL_STATUS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_REPORTS_SUCCESS: {
      return {
        ...state,
        riskReports: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_REPORTS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_REPORTS)
      };
    }
    case RiskAssessmentActionTypes.GET_REPORTS_FAILURE: {
      return {
        ...state,
        riskReports: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_REPORTS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_REPORTS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.CREATE_RISK_REPORT_SUCCESS:
    case RiskAssessmentActionTypes.UPDATE_REPORT: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.CREATE_RISK_REPORT),
        riskReports: {
          ...state.riskReports,
          results: [action.payload, ...state.riskReports.results]
        }
      };
    }
    case RiskAssessmentActionTypes.GET_OCR_RESULTS_SUCCESS: {
      return {
        ...state,
        ocrResults: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_OCR_RESULTS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_OCR_RESULTS)
      };
    }
    case RiskAssessmentActionTypes.GET_OCR_RESULTS_FAILURE: {
      return {
        ...state,
        ocrResults: null,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_OCR_RESULTS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_OCR_RESULTS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_FACE_RESULTS_SUCCESS: {
      return {
        ...state,
        faceResults: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_FACE_RESULTS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_FACE_RESULTS)
      };
    }
    case RiskAssessmentActionTypes.GET_FACE_RESULTS_FAILURE: {
      return {
        ...state,
        faceResults: null,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_FACE_RESULTS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_FACE_RESULTS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_OCR_CONCLUSIONS_SUCCESS: {
      return {
        ...state,
        ocrConclusions: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.GET_FACE_CONCLUSIONS_SUCCESS: {
      return {
        ...state,
        faceConclusions: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.GET_CONCLUSIONS_FAILURE: {
      return {
        ...state,
        faceConclusions: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        ocrConclusions: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_CONCLUSIONS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_CONCLUSIONS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS_SUCCESS: {
      return {
        ...state,
        latestOcrConclusions: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS_FAILURE: {
      return {
        ...state,
        latestOcrConclusions: null,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS_SUCCESS: {
      return {
        ...state,
        latestFaceConclusions: action.payload,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS_FAILURE: {
      return {
        ...state,
        latestFaceConclusions: null,
        loading: clearLoading(state, RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION)
      };
    }
    case RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS_SUCCESS: {
      let newState = {
        ...state,
        latestOcrConclusions: {
          ...state.latestOcrConclusions,
          [action.payload.crp]: action.payload
        },
      };
      if (action.payload.verificationType === VerificationType.FACE) {
        newState = {
          ...state,
          latestFaceConclusions: {
            ...state.latestFaceConclusions,
            [action.payload.crp]: action.payload
          },
        };
      }
      return {
        ...newState,
        loading: clearLoading(state, RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS)
      };
    }
    case RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS),
        err: state.err.filter((item: RiskAssessmentError) =>
          item.action !== RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS),
      };
    }
    case RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS),
        err: [...state.err, <RiskAssessmentError>{
          action: RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS,
          detail: action.payload
        }]
      };
    }
    case RiskAssessmentActionTypes.GET_CONCLUSIONS:
    case RiskAssessmentActionTypes.GET_APPROVAL_STATUS:
    case RiskAssessmentActionTypes.GET_REPORTS:
    case RiskAssessmentActionTypes.GET_OCR_RESULTS:
    case RiskAssessmentActionTypes.GET_FACE_RESULTS:
    case RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS:
    case RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS:
    case RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION:
    case RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS:
    case RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS:
    case RiskAssessmentActionTypes.CREATE_RISK_REPORT: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    default: {
      return state;
    }
  }
}

const riskAssessment = (state: AppState) => state.riskAssessment;
export const Selectors = {
  getErrors: createSelector(
    riskAssessment,
    (state: State) => state.err
  ),
  getLoading: createSelector(
    riskAssessment,
    (state: State) => state.loading
  ),
  getApprovalStatus: createSelector(
    riskAssessment,
    (state: State) => state.approvalStatuses
  ),
  getReports: createSelector(
    riskAssessment,
    (state: State) => state.riskReports
  ),
  getLatestReport: createSelector(
    riskAssessment,
    (state: State) => state.riskReports.results.length ? getLatestRiskReport(state.riskReports.results) : null
  ),
  getOcrResults: createSelector(
    riskAssessment,
    (state: State) => state.ocrResults
  ),
  getFaceResults: createSelector(
    riskAssessment,
    (state: State) => state.faceResults
  ),
  getOcrConclusions: createSelector(
    riskAssessment,
    (state: State) => state.ocrConclusions
  ),
  getFaceConclusions: createSelector(
    riskAssessment,
    (state: State) => state.faceConclusions
  ),
  getLatestOcrConclusions: createSelector(
    riskAssessment,
    (state: State) => state.latestOcrConclusions
  ),
  getLatestFaceConclusions: createSelector(
    riskAssessment,
    (state: State) => state.latestFaceConclusions
  ),
};

function getLatestRiskReport(riskReportList: Array<RiskReport>): RiskReport {
  riskReportList.sort((reportA: RiskReport, reportB: RiskReport) => {
    if (reportA.id > reportB.id) {
      return -1;
    } else if (reportB.id > reportA.id) {
      return 1;
    }

    return 0;
  });

  return riskReportList[0];
}

function clearLoading(authState: State, action: RiskAssessmentActionTypes): Array<RiskAssessmentActionTypes> {
  return authState.loading.filter((loadingMarker: RiskAssessmentActionTypes) => loadingMarker !== action);
}
